import axios, { AxiosRequestConfig } from 'axios';

// ─── Types ────────────────────────────────────────────────────────────

/** Types de source possibles */
export type RAGSourceType = 'note' | 'entree' | 'entreeShare' | 'document';

/** Résultat de recherche individuel */
export interface RAGSearchResult {
    /** Texte du chunk trouvé */
    text: string;
    /** Score de pertinence (0-1) */
    score: number;
    /** ID de la note source */
    noteId: string;
    /** Titre de la note source */
    noteTitle: string;
    /** Nom du périmètre */
    perimetreName: string;
    /** ID du périmètre */
    perimetreId: string;
    /** Heading de section (optionnel) */
    sectionHeading?: string;
    /** Index du chunk dans la note */
    chunkIndex: number;
    /** Type de source : 'note' | 'entree' | 'entreeShare' */
    sourceType: string;
    /** ID de la source */
    sourceId: string;
    /**
     * Entrées de la requête que l'utilisateur a taguées dans ce passage. Elles ont
     * relevé le score de ce résultat : les afficher est ce qui rend le pourcentage de
     * pertinence honnête, plutôt qu'un chiffre qui monte sans raison visible.
     */
    matchedEntreeCles?: string[];
    /** Concept partagé : le vocabulaire partagé qui le porte (C25 SP1 ter). */
    thesaurusShareId?: string;
}

/** Un terme remplacé par le terme préféré du thésaurus (renvoi USE). */
export interface RAGElargissement {
    /** Terme tel que l'utilisateur l'a écrit. */
    de: string;
    /** Terme préféré employé à sa place. */
    vers: string;
}

/** Réponse complète de la recherche RAG */
export interface RAGSearchResponse {
    results: RAGSearchResult[];
    query: string;
    mode: 'hybrid' | 'vector' | 'fulltext';
    totalFound: number;
    duration: number;
    /**
     * Renvois USE appliqués à la requête. À montrer : la recherche a cherché autre chose
     * que ce qui a été tapé, et l'utilisateur doit pouvoir le voir et le refuser.
     */
    elargissements?: RAGElargissement[];
}

/** Paramètres de la recherche RAG */
export interface RAGSearchParams {
    /** Requête en langage naturel */
    query: string;
    /** Filtrer par périmètre(s) */
    perimetreIds?: string[];
    /** Filtrer par note(s) spécifique(s) */
    noteIds?: string[];
    /** Filtrer par document(s) (C19) : extraits de ces fichiers seulement */
    fileIds?: string[];
    /** Filtrer par type(s) de source : 'note', 'entree', 'entreeShare', 'document' */
    sourceTypes?: string[];
    /**
     * Vocabulaires partagés dont les concepts (`entreeShare`) sont admis (C25 SP1 ter) :
     * sans eux, le serveur ne rend aucun concept partagé, quoi que `sourceTypes` demande.
     */
    thesaurusShareIds?: string[];
    /** Mode de recherche (défaut: hybrid) */
    mode?: 'hybrid' | 'vector' | 'fulltext';
    /** Nombre max de résultats (défaut: 5) */
    top?: number;
    /** Score minimum (défaut: 0.3) */
    minScore?: number;
    /** Items de contexte injectés par @mentions */
    contextItems?: { type: string; label: string; content: string }[];
    /**
     * Appliquer les renvois USE du thésaurus (défaut : true). Passer `false` pour
     * chercher exactement les termes saisis, sans substitution.
     */
    elargirRenvois?: boolean;
}

/** Health check */
export interface RAGHealthResponse {
    status: string;
    services: {
        embedding: { ok: boolean; model: string };
        search: { ok: boolean; mode: string };
    };
}

// ─── Config ───────────────────────────────────────────────────────────

const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json'
    }
});

// ─── Service API ──────────────────────────────────────────────────────

/**
 * Service API pour la recherche RAG (Retrieval-Augmented Generation)
 * Backend : Atlas Vector Search ($vectorSearch) sur MongoDB Atlas.
 */
export const ragSearchAPI = {
    /**
     * Lance une recherche sémantique dans les notes de l'utilisateur
     */
    search: async (params: RAGSearchParams, accessToken: string): Promise<RAGSearchResponse> => {
        try {
            const response = await axios.post('/api/rag/search', params, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            console.error('[ragSearchAPI] Erreur recherche:', error);
            if (error.response?.data) {
                console.error('[ragSearchAPI] Détails serveur:', JSON.stringify(error.response.data, null, 2));
            }
            if (error.response?.status === 401) {
                throw new Error('Authentification requise');
            }
            const serverError = error.response?.data?.error;
            const serverMsg = error.response?.data?.message;
            throw new Error(serverError || serverMsg || 'Erreur de recherche RAG');
        }
    },

    /**
     * Vérifie la santé du service RAG
     */
    healthCheck: async (): Promise<RAGHealthResponse> => {
        try {
            const response = await axios.get('/api/rag/search/health');
            return response.data.data;
        } catch (error: any) {
            throw new Error('Service RAG indisponible');
        }
    }
};
